import { Instagram, Linkedin, Music2, Youtube } from 'lucide-react';
import type { Locale } from './Navigation';
import { siteConfig } from '../siteConfig';

type SiteFooterProps = {
  locale: Locale;
  productContext?: 'fampo';
};

const copy = {
  sv: {
    tagline: 'Digitala produkter · System · AI & automation',
    services: 'Tjänster',
    products: 'Produkter',
    company: 'Företag',
    contact: 'Kontakt',
    servicesItems: [
      ['/apputveckling/', 'Apputveckling'],
      ['/digitalisering/', 'Digitalisering & interna system'],
      ['/ai-automation/', 'AI & automation'],
    ],
    productsItems: [
      ['/fampo/', 'FamPo'],
      ['/#arbete', 'Din Förening'],
    ],
    companyItems: [
      ['/om-keevo/', 'Om Keevo'],
      ['/#kontakt', 'Kontakt'],
    ],
    location: 'Huvudkontor Sverige',
    sole: 'Enskild firma',
    orgLabel: 'Org.nr',
    vatLabel: 'Momsnr',
    fampoBy: 'FamPo är utvecklad av Keevo i Sverige.',
    fampoResources: 'FamPo',
    fampoItems: [
      ['/fampo/support/', 'Support'],
      ['/fampo/privacy/', 'Integritetspolicy'],
      ['/fampo/delete-account/', 'Radera konto'],
    ],
  },
  en: {
    tagline: 'Digital products · Systems · AI & automation',
    services: 'Services',
    products: 'Products',
    company: 'Company',
    contact: 'Contact',
    servicesItems: [
      ['/en/app-development/', 'App development'],
      ['/en/digitization/', 'Digitization & internal systems'],
      ['/en/ai-automation/', 'AI & automation'],
    ],
    productsItems: [
      ['/en/fampo/', 'FamPo'],
      ['/en/#arbete', 'Din Förening'],
    ],
    companyItems: [
      ['/en/about/', 'About Keevo'],
      ['/en/#kontakt', 'Contact'],
    ],
    location: 'Headquartered in Sweden',
    sole: 'Sole proprietorship',
    orgLabel: 'Org. no.',
    vatLabel: 'VAT no.',
    fampoBy: 'FamPo is developed by Keevo in Sweden.',
    fampoResources: 'FamPo',
    fampoItems: [
      ['/en/fampo/support/', 'Support'],
      ['/en/fampo/privacy/', 'Privacy policy'],
      ['/en/fampo/delete-account/', 'Delete account'],
    ],
  },
} as const;

export function SiteFooter({ locale, productContext }: SiteFooterProps) {
  const t = copy[locale];
  const homePath = locale === 'en' ? '/en/' : '/';
  const activeSocial = [
    ['TikTok', siteConfig.social.tiktok, Music2],
    ['Instagram', siteConfig.social.instagram, Instagram],
    ['LinkedIn', siteConfig.social.linkedin, Linkedin],
    ['YouTube', siteConfig.social.youtube, Youtube],
  ].filter(([, url]) => Boolean(url)) as Array<[string, string, typeof Music2]>;

  return (
    <footer className="border-t border-[#DEDCE3] bg-[#F7F6F2] px-6 py-14 text-[#12141A] md:px-8 lg:px-16">
      <div className="mx-auto max-w-[1450px]">
        <div className={`grid gap-10 pb-12 sm:grid-cols-2 ${productContext === 'fampo' ? 'lg:grid-cols-[1.25fr_.9fr_.9fr_.9fr_.9fr]' : 'lg:grid-cols-[1.25fr_1fr_1fr_1fr]'}`}>
          <div>
            <a href={homePath} aria-label={locale === 'sv' ? 'Keevo startsida' : 'Keevo home'} className="inline-flex">
              <span className="text-[27px] tracking-[-0.04em]" style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700 }}>
                <span className="text-[#12141A]">k</span>
                <span className="text-[#5B4BFF]">ee</span>
                <span className="text-[#12141A]">vo</span>
                <span className="text-[#5B4BFF]">.</span>
              </span>
            </a>
            <p className="mt-3 max-w-[330px] text-sm leading-6 text-[#71757E]">
              {productContext === 'fampo' ? t.fampoBy : t.tagline}
            </p>
            <div className="mt-5 space-y-1 text-sm text-[#71757E]">
              <p>{t.location}</p>
              <p>{t.sole}</p>
              {siteConfig.organisationNumber && <p>{t.orgLabel}: {siteConfig.organisationNumber}</p>}
              {siteConfig.vatNumber && <p>{t.vatLabel}: {siteConfig.vatNumber}</p>}
            </div>
          </div>

          <FooterColumn title={t.services} items={t.servicesItems} />
          <FooterColumn title={t.products} items={t.productsItems} />
          {productContext === 'fampo' && <FooterColumn title={t.fampoResources} items={t.fampoItems} />}
          <FooterColumn title={t.company} items={t.companyItems} />
        </div>

        <div className="flex flex-col gap-5 border-t border-[#DEDCE3] pt-7 text-sm text-[#7B7D85] sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <span>© 2026 Keevo</span>
            <a href={`mailto:${siteConfig.email}`} className="font-semibold text-[#5B4BFF] transition hover:text-[#4B37E0]">
              {siteConfig.email}
            </a>
          </div>

          {activeSocial.length > 0 && (
            <div className="flex items-center gap-2">
              {activeSocial.map(([name, url, Icon]) => (
                <a
                  key={name}
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={name}
                  className="grid h-9 w-9 place-items-center rounded-full border border-[#D8D5DD] transition hover:border-[#BEB7F4] hover:text-[#5B4BFF]"
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </footer>
  );
}

function FooterColumn({
  title,
  items,
}: {
  title: string;
  items: ReadonlyArray<readonly [string, string]>;
}) {
  return (
    <div>
      <h2 className="text-xs font-bold uppercase tracking-[0.13em] text-[#8A8B92]">{title}</h2>
      <nav className="mt-4 flex flex-col gap-3" aria-label={title}>
        {items.map(([href, label]) => (
          <a
            key={href + label}
            href={href}
            className="text-sm font-semibold leading-6 text-[#50535C] transition hover:text-[#5B4BFF]"
          >
            {label}
          </a>
        ))}
      </nav>
    </div>
  );
}
